import React, { createContext, useContext, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID_POPUP = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

// Context for popup open/close
const PopupFormContext = createContext<{ open: boolean; openPopup: () => void; closePopup: () => void }>({ open: false, openPopup: () => {}, closePopup: () => {} });

export const usePopupForm = () => useContext(PopupFormContext);

export const PopupFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <PopupFormContext.Provider value={{ open, openPopup: () => setOpen(true), closePopup: () => setOpen(false) }}>
      {children}
      <PopupForm />
    </PopupFormContext.Provider>
  );
};

const PopupForm = () => {
  const { open, closePopup } = usePopupForm();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phoneNumber || ''
      }));
    }
  }, [user, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID_POPUP,
      formData,
      EMAILJS_PUBLIC_KEY
    )
      .then(() => {
        setIsSubmitting(false);
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          closePopup();
        }, 3000);
      })
      .catch(() => {
        setError(t('contact.sendingFailed'));
        setIsSubmitting(false);
      });
  };

  const WHATSAPP_PHONE = import.meta.env.VITE_WHATSAPP_PHONE;

  if (!open) {
    return (
      <button
        onClick={usePopupForm().openPopup}
        className="fixed bottom-4 right-4 bg-blue-600 text-white py-3 px-6 rounded-full shadow-lg hover:bg-blue-700 transition-transform transform hover:scale-105 z-50"
      >
        {t('contact.testRideOnUs')}
      </button>
    );
  }

  if (success) {
    return (
      <div className="fixed bottom-0 right-0 left-0 lg:left-auto lg:right-4 lg:bottom-4 bg-white rounded-t-lg lg:rounded-lg shadow-2xl p-8 w-full max-w-lg z-50 flex flex-col items-center justify-center text-center" dir="rtl">
        <svg className="w-16 h-16 text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <h3 className="text-2xl font-bold mb-2 text-green-700">{t('contact.messageSent')}</h3>
        <p className="text-lg text-gray-700 mb-4">{t('contact.thankYou')}</p>
        <p className="text-md text-gray-700 mb-2">{t('contact.impatient')}</p>
        <a
          href={`https://wa.me/${WHATSAPP_PHONE}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-green-600 hover:text-green-800 font-semibold text-lg mb-4"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6 ml-2"><path d="M20.52 3.48A11.77 11.77 0 0 0 12 0C5.37 0 0 5.37 0 12a11.9 11.9 0 0 0 1.64 6.01L0 24l6.18-1.62A11.93 11.93 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52zM12 22c-1.7 0-3.36-.44-4.8-1.28l-.34-.2-3.67.96.98-3.58-.22-.36A9.94 9.94 0 0 1 2 12C2 6.48 6.48 2 12 2c2.65 0 5.15 1.03 7.04 2.92A9.93 9.93 0 0 1 22 12c0 5.52-4.48 10-10 10zm5.2-7.6c-.28-.14-1.65-.81-1.9-.9-.25-.09-.43-.14-.61.14-.18.28-.7.9-.86 1.08-.16.18-.32.2-.6.07-.28-.14-1.18-.43-2.25-1.37-.83-.74-1.39-1.65-1.55-1.93-.16-.28-.02-.43.12-.57.13-.13.28-.34.42-.51.14-.17.18-.29.28-.48.09-.19.05-.36-.02-.5-.07-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47-.16-.01-.35-.01-.54-.01-.19 0-.5.07-.76.34-.26.27-1 1-.97 2.43.03 1.43 1.03 2.81 1.18 3 .15.19 2.03 3.1 4.93 4.23.69.3 1.23.48 1.65.61.69.22 1.32.19 1.82.12.56-.08 1.65-.67 1.88-1.32.23-.65.23-1.2.16-1.32-.07-.12-.25-.19-.53-.33z"/></svg>
          WhatsApp
        </a>
        <button onClick={() => { setSuccess(false); closePopup(); }} className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition">{t('common.close')}</button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed bottom-0 right-0 left-0 lg:left-auto lg:right-4 lg:bottom-4 bg-white rounded-t-lg lg:rounded-lg shadow-2xl p-8 w-full max-w-lg z-50 flex flex-col items-center justify-center text-center" dir="rtl">
        <svg className="w-16 h-16 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        <h3 className="text-2xl font-bold mb-2 text-red-700">{t('common.error')}</h3>
        <p className="text-lg text-gray-700 mb-4">{error}</p>
        <p className="text-md text-gray-700 mb-2">{t('contact.impatient')}</p>
        <a
          href={`https://wa.me/${WHATSAPP_PHONE}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-green-600 hover:text-green-800 font-semibold text-lg mb-4"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6 ml-2"><path d="M20.52 3.48A11.77 11.77 0 0 0 12 0C5.37 0 0 5.37 0 12a11.9 11.9 0 0 0 1.64 6.01L0 24l6.18-1.62A11.93 11.93 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52zM12 22c-1.7 0-3.36-.44-4.8-1.28l-.34-.2-3.67.96.98-3.58-.22-.36A9.94 9.94 0 0 1 2 12C2 6.48 6.48 2 12 2c2.65 0 5.15 1.03 7.04 2.92A9.93 9.93 0 0 1 22 12c0 5.52-4.48 10-10 10zm5.2-7.6c-.28-.14-1.65-.81-1.9-.9-.25-.09-.43-.14-.61.14-.18.28-.7.9-.86 1.08-.16.18-.32.2-.6.07-.28-.14-1.18-.43-2.25-1.37-.83-.74-1.39-1.65-1.55-1.93-.16-.28-.02-.43.12-.57.13-.13.28-.34.42-.51.14-.17.18-.29.28-.48.09-.19.05-.36-.02-.5-.07-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47-.16-.01-.35-.01-.54-.01-.19 0-.5.07-.76.34-.26.27-1 1-.97 2.43.03 1.43 1.03 2.81 1.18 3 .15.19 2.03 3.1 4.93 4.23.69.3 1.23.48 1.65.61.69.22 1.32.19 1.82.12.56-.08 1.65-.67 1.88-1.32.23-.65.23-1.2.16-1.32-.07-.12-.25-.19-.53-.33z"/></svg>
          WhatsApp
        </a>
        <button onClick={() => { setError(null); }} className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition">{t('common.close')}</button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 left-0 lg:left-auto lg:right-4 lg:bottom-4 bg-white rounded-t-lg lg:rounded-lg shadow-2xl p-6 w-full max-w-lg z-50 transition-all duration-300" dir="rtl">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-gray-800">{t('contact.talkToUs')}</h3>
        <button onClick={closePopup} className="text-gray-500 hover:text-gray-800">&times;</button>
      </div>
      {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="popup-name" className="sr-only">{t('contact.name')}</label>
          <input type="text" id="popup-name" name="name" value={formData.name} onChange={handleChange} placeholder={t('contact.name')} className="w-full px-3 py-2 border rounded-lg" required />
        </div>
        <div className="mb-4">
          <label htmlFor="popup-email" className="sr-only">{t('contact.email')}</label>
          <input type="email" id="popup-email" name="email" value={formData.email} onChange={handleChange} placeholder={t('contact.email')} className="w-full px-3 py-2 border rounded-lg" required />
        </div>
        <div className="mb-4">
          <label htmlFor="popup-phone" className="sr-only">{t('contact.phone')}</label>
          <input type="tel" id="popup-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('contact.phone')} className="w-full px-3 py-2 border rounded-lg text-right placeholder:text-right" required />
        </div>
        <div className="mb-4">
          <label htmlFor="popup-message" className="sr-only">{t('contact.message')}</label>
          <textarea id="popup-message" name="message" value={formData.message} onChange={handleChange} placeholder={t('contact.writeMessage')} rows={3} className="w-full px-3 py-2 border rounded-lg"></textarea>
        </div>
        <button type="submit" className="w-full bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-transform" disabled={isSubmitting}>
          {isSubmitting ? t('contact.sending') : t('contact.submit')}
        </button>
      </form>
    </div>
  );
};

export default PopupForm; 